/**
 * Element chain detection for BIM-style selection cycling.
 *
 * Builds the ordered Tab-cycle steps under the cursor: each overlapping hit
 * element contributes a "single element" step, followed (when the element is
 * a linear member connected to same-category siblings) by a "chain" step that
 * selects the whole connected run — mirroring BIM's wall/line chain
 * selection.
 */

import { BIMElement } from '../core/types';

export type SelectionStepKind = 'element' | 'chain';

export interface SelectionStep {
    kind: SelectionStepKind;
    ids: string[];
}

/** Endpoint proximity for chain joining, in model meters (~10 cm). */
const CHAIN_ENDPOINT_TOL = 0.1;
const CHAIN_ENDPOINT_TOL_SQ = CHAIN_ENDPOINT_TOL * CHAIN_ENDPOINT_TOL;

/**
 * Endpoints of a linear element (walls, beams, ducts, pipes, lines...).
 * Returns null for area/volume/point geometries which never chain.
 */
function linearEndpoints(geometryData: any): [number, number, number][] | null {
    if (!geometryData) return null;
    if (Array.isArray(geometryData.start) && Array.isArray(geometryData.end)) {
        return [geometryData.start, geometryData.end];
    }
    if (Array.isArray(geometryData.path) && geometryData.path.length >= 2) {
        return [geometryData.path[0], geometryData.path[geometryData.path.length - 1]];
    }
    return null;
}

function endpointsClose(
    a: [number, number, number],
    b: [number, number, number]
): boolean {
    const dx = a[0] - b[0];
    const dy = a[1] - b[1];
    const dz = a[2] - b[2];
    return dx * dx + dy * dy + dz * dz <= CHAIN_ENDPOINT_TOL_SQ;
}

/**
 * All same-category linear elements touching `startId` at their endpoints,
 * transitively (the "chain"). Returns at least the element itself; a single
 * element result means it is not part of a chain.
 */
export function findConnectedChain(
    elements: Map<string, BIMElement>,
    startId: string
): string[] {
    const start = elements.get(startId);
    if (!start) return [startId];

    const startEnds = linearEndpoints((start as any).geometryData);
    if (!startEnds) return [startId];

    const chain = new Set<string>([startId]);
    // BFS frontier: open endpoints of chain members not yet expanded.
    const frontier: [number, number, number][] = [...startEnds];

    while (frontier.length > 0) {
        const endpoint = frontier.pop()!;
        elements.forEach((element, id) => {
            if (chain.has(id)) return;
            if (element.category !== start.category) return;
            const ends = linearEndpoints((element as any).geometryData);
            if (!ends) return;
            let joined = false;
            for (const end of ends) {
                if (endpointsClose(end, endpoint)) {
                    joined = true;
                    break;
                }
            }
            if (!joined) return;
            chain.add(id);
            frontier.push(...ends);
        });
    }

    return Array.from(chain);
}

/**
 * Build the Tab-cycle steps for the elements hit under the pointer, in
 * front-to-back hit order: [element, chain?, element, chain?, ...].
 * Chain steps are only appended when the element actually joins a longer run.
 */
export function buildSelectionSteps(
    elements: Map<string, BIMElement>,
    hitIds: string[]
): SelectionStep[] {
    const steps: SelectionStep[] = [];
    const seen = new Set<string>();

    for (const id of hitIds) {
        if (!id || seen.has(id)) continue;
        seen.add(id);
        steps.push({ kind: 'element', ids: [id] });

        const chain = findConnectedChain(elements, id);
        if (chain.length > 1) {
            const chainKey = [...chain].sort().join('|');
            if (!seen.has(chainKey)) {
                seen.add(chainKey);
                steps.push({ kind: 'chain', ids: chain });
            }
        }
    }

    return steps;
}
